/**
 * VideoSpeedDatingPage.jsx
 * 
 * Stable resolver entry point for Video Speed Dating.
 * Routes to /speed-dating/lobby/:event_id based on region.
 * 
 * Regions (V1 LOCKED):
 * 1. United States (us) - America/Chicago
 * 2. South America & Caribbean (south_america_islands) - America/Sao_Paulo
 * 3. Africa (africa) - Africa/Lagos
 * 
 * NEVER returns "event not found" during normal operation.
 */

import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import apiClient from "../api/client";
import { Video, Clock, MapPin, ArrowLeft, RefreshCw, Calendar, MessageCircle, Heart, Users } from "lucide-react";
import { trackEvent } from "../api/analytics";
import { PostEventReflectionModal } from "../components/reflection/PostEventReflectionModal";


// V1 Region mapping
const REGION_OPTIONS = [
  { key: "us", name: "United States", timezone: "America/Chicago" },
  { key: "south_america_islands", name: "South America & Caribbean", timezone: "America/Sao_Paulo" },
  { key: "africa", name: "Africa", timezone: "Africa/Lagos" },
];

// Timezone to region inference (simplified)
function inferRegionFromTimezone(tz) {
  if (!tz) return "us"; // Default fallback
  
  const tzLower = tz.toLowerCase();
  
  // US timezones
  if (tzLower.includes("america/") && 
      (tzLower.includes("new_york") || tzLower.includes("chicago") || 
       tzLower.includes("denver") || tzLower.includes("los_angeles") ||
       tzLower.includes("phoenix") || tzLower.includes("anchorage"))) {
    return "us";
  }
  
  // South America / Caribbean
  if (tzLower.includes("america/sao_paulo") || tzLower.includes("america/buenos_aires") ||
      tzLower.includes("america/santiago") || tzLower.includes("america/bogota") ||
      tzLower.includes("america/lima") || tzLower.includes("america/caracas") ||
      tzLower.includes("america/havana") || tzLower.includes("america/jamaica") ||
      tzLower.includes("america/puerto_rico") || tzLower.includes("america/barbados")) {
    return "south_america_islands";
  }
  
  // Africa
  if (tzLower.includes("africa/")) {
    return "africa";
  }
  
  return "us"; // Default
}

// =============================================================================
// LOADING SCREEN
// =============================================================================

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex flex-col items-center justify-center px-4">
      <RefreshCw className="w-12 h-12 text-purple-400 animate-spin mb-4" />
      <p className="text-white/60">Finding your regional event...</p>
    </div>
  );
}

// =============================================================================
// EVENT CARD
// =============================================================================

function EventCard({ event, onSelect }) {
  const getStatusBadge = () => {
    switch (event.status) {
      case "live":
        return (
          <span className="px-3 py-1 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 text-xs font-medium animate-pulse">
            🔴 LIVE NOW
          </span>
        );
      case "join_open":
        return (
          <span className="px-3 py-1 rounded-full bg-green-500/20 border border-green-500/50 text-green-300 text-xs font-medium">
            ✅ Join Open
          </span>
        );
      case "scheduled":
        return (
          <span className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-300 text-xs font-medium">
            ⏰ Upcoming
          </span>
        );
      case "ended":
        return (
          <span className="px-3 py-1 rounded-full bg-slate-500/20 border border-slate-500/50 text-slate-300 text-xs font-medium">
            Ended
          </span>
        );
      default:
        return null;
    }
  };

  // Format time in region timezone
  const formatLocalTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: event.timezone,
      });
    } catch {
      return "8:00 PM";
    }
  };

  return (
    <div 
      className="rounded-2xl bg-gradient-to-br from-purple-500/10 via-slate-900/80 to-slate-900 border border-purple-500/30 p-6 cursor-pointer hover:border-purple-400/50 transition-all"
      onClick={onSelect}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white mb-1">{event.title}</h3>
          <p className="text-white/60 text-sm flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            {event.region_name}
          </p>
        </div>
        {getStatusBadge()}
      </div>

      <div className="flex items-center gap-4 text-sm text-white/50 mb-4">
        <span className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {formatLocalTime(event.scheduled_start)} local
        </span>
        <span className="flex items-center gap-1">
          <Video className="w-4 h-4" />
          {event.duration_minutes} min
        </span>
      </div>

      <button className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:from-purple-600 hover:to-pink-600 transition-all">
        {event.status === "live" ? "Join Now" : event.status === "join_open" ? "Enter Lobby" : "View Details"}
      </button>
    </div>
  );
}

// =============================================================================
// ENDED EVENT SCREEN (B7 - Dignity-first UX)
// =============================================================================

function EndedEventScreen({ event, nextEventHours, onGoToText, onBrowseMatches, onBrowseEvents }) {
  // Post-Event Reflection modal
  const [showReflection, setShowReflection] = useState(false);
  const hasShownReflectionRef = useRef(false);
  
  // Show reflection modal once on mount
  useEffect(() => {
    if (!hasShownReflectionRef.current) {
      hasShownReflectionRef.current = true;
      const timer = setTimeout(() => setShowReflection(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-800 flex items-center justify-center">
          <Video className="w-10 h-10 text-white/50" />
        </div>

        {/* Headline - Dignity first, no rejection */}
        <h1 className="text-2xl font-bold text-white mb-2">Thanks for showing up</h1>
        <p className="text-white/60 mb-2">
          Today's video event for {event.region_name} has ended.
        </p>
        
        {nextEventHours && (
          <p className="text-purple-300 text-sm mb-6">
            Next event in ~{Math.round(nextEventHours)} hours
          </p>
        )}

        {/* Soft redirect to text */}
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20">
          <p className="text-white/80 text-sm">
            Want something softer? Text is always open.
          </p>
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          <button
            onClick={onGoToText}
            className="w-full py-4 px-6 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold text-lg hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg shadow-pink-500/25"
          >
            Go to Text Speed Dating
          </button>

          <button
            onClick={onBrowseMatches}
            className="w-full py-3 px-6 rounded-xl bg-slate-800 text-white/70 hover:bg-slate-700 transition-all flex items-center justify-center gap-2"
          >
            <Heart className="w-5 h-5" />
            Browse Matches
          </button>

          <button
            onClick={onBrowseEvents}
            className="w-full py-3 px-6 rounded-xl bg-slate-800/50 text-white/50 hover:bg-slate-700/50 transition-all flex items-center justify-center gap-2"
          >
            <Calendar className="w-5 h-5" />
            Browse Events
          </button>
        </div>
      </div>
      
      {/* Post-Event Reflection Modal */}
      <PostEventReflectionModal
        open={showReflection}
        onClose={() => setShowReflection(false)}
        eventType="video_speed_dating"
        eventId={event?.id}
      />
    </div>
  );
}

// =============================================================================
// REGION SELECTOR
// =============================================================================

function RegionSelector({ currentRegion, onSelectRegion }) {
  const [isOpen, setIsOpen] = useState(false);

  const current = REGION_OPTIONS.find(r => r.key === currentRegion) || REGION_OPTIONS[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 text-white/70 hover:bg-slate-700 transition-all"
      >
        <MapPin className="w-4 h-4" />
        <span>{current.name}</span>
        <span className="text-white/40">▼</span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 rounded-xl bg-slate-800 border border-slate-700 shadow-xl z-50">
          {REGION_OPTIONS.map((region) => (
            <button
              key={region.key}
              onClick={() => {
                onSelectRegion(region.key);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left hover:bg-slate-700 first:rounded-t-xl last:rounded-b-xl transition-all ${
                region.key === currentRegion ? "bg-purple-500/20 text-purple-300" : "text-white/70"
              }`}
            >
              <div className="font-medium">{region.name}</div>
              <div className="text-xs text-white/40">{region.timezone}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function VideoSpeedDatingPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regionKey, setRegionKey] = useState(null);
  const [event, setEvent] = useState(null);
  const [nextEventHours, setNextEventHours] = useState(null);

  // Determine region on mount
  useEffect(() => {
    // Check user preference first
    const userPreferredRegion = user?.preferred_region_key;
    
    if (userPreferredRegion && REGION_OPTIONS.find(r => r.key === userPreferredRegion)) {
      setRegionKey(userPreferredRegion);
    } else {
      // Infer from browser timezone
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const inferred = inferRegionFromTimezone(browserTz);
      setRegionKey(inferred);
    }
  }, [user]);

  // Fetch event when region is set
  useEffect(() => {
    if (!regionKey || !token) return;

    const fetchEvent = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(
          `/speed-dating/video/current?region_key=${regionKey}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setEvent(res.data.event);
        setNextEventHours(res.data.next_event_in_hours);

        trackEvent("video_event_resolved", {
          region_key: regionKey,
          event_id: res.data.event?.id,
          status: res.data.event?.status,
        });
      } catch (err) {
        console.error("[VideoSpeedDating] Failed to fetch event:", err);
        const detail = err.response?.data?.detail;
        if (typeof detail === "object") {
          setError(detail.message || "Failed to load event");
        } else {
          setError(detail || "Failed to load event");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [regionKey, token]);

  // Change region
  const handleRegionChange = (newRegion) => {
    setRegionKey(newRegion);
  };

  // Navigate to lobby
  const handleSelectEvent = () => {
    if (event) {
      navigate(`/speed-dating/lobby/${event.id}`);
    }
  };

  // Render
  if (loading) {
    return <LoadingScreen />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <Video className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-full bg-purple-500 text-white font-medium hover:bg-purple-600 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Event ended - show dignity-first end screen (B7)
  if (event && event.status === "ended") {
    return (
      <EndedEventScreen
        event={event}
        nextEventHours={nextEventHours}
        onGoToText={() => navigate("/text-speed-dating")}
        onBrowseMatches={() => navigate("/inbox")}
        onBrowseEvents={() => navigate("/events/speed-dating")}
      />
    );
  }

  // Main view
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black px-4 py-6">
      {/* Header */}
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          {/* FIX #4: Back to Speed Dating Hub */}
          <button
            onClick={() => navigate("/speed-dating")}
            className="text-white/50 hover:text-white/70 flex items-center gap-1 transition-colors"
            data-testid="video-speed-dating-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>

          <RegionSelector
            currentRegion={regionKey}
            onSelectRegion={handleRegionChange}
          />
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Video className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Video Speed Dating
          </h1>
          <p className="text-white/60 text-sm">
            Once per day. Real faces. Real connections.
          </p>
        </div>

        {/* Event Card */}
        {event && (
          <EventCard
            event={event}
            onSelect={handleSelectEvent}
          />
        )}

        {/* Rules snippet */}
        <div className="mt-6 p-4 rounded-xl bg-slate-900/50 border border-slate-800">
          <h3 className="text-white/80 font-medium mb-2 text-sm flex items-center gap-2">
            <Users className="w-4 h-4" />
            What to expect:
          </h3>
          <ul className="text-white/50 text-xs space-y-1">
            <li>• Short rounds. Clear endings.</li>
            <li>• Respect always.</li>
            <li>• Choices are private.</li>
            <li>• Mutual interest creates a match.</li>
          </ul>
        </div>

        {/* Text Speed Dating fallback */}
        <div className="mt-6 text-center">
          <p className="text-white/40 text-sm mb-2">
            Not ready for video?
          </p>
          <button
            onClick={() => navigate("/text-speed-dating")}
            className="text-pink-400 hover:text-pink-300 text-sm flex items-center justify-center gap-1 mx-auto"
          >
            <MessageCircle className="w-4 h-4" />
            Try Text Speed Dating (always open)
          </button>
        </div>
      </div>
    </div>
  );
}
